import { FusionCustomInput, FusionInputField } from 'components';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { useEffect, useState } from 'react';
import { Container, Dropdown, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetAllRolesQuery } from 'services/roles/roles.api';
import { checkFusionUserEmail } from 'services/user/user.service';
import { selectUserEmail } from 'store/selectors/User/userSelectors';
import styles from './UserEditor.module.css';
import { FaChevronDown } from 'react-icons/fa6';

// The value is the backend's maintenanceOperatorLevel, not a list position.
const MAINTNEANCE_OPTIONS = [
  { value: 0, label: 'No Maintenance Access' },
  { value: 1, label: 'Maintenance Operator' },
  { value: 2, label: 'Maintenance Admin' },
];

// @ts-check

/**
 * A clear default user data object.
 * @typedef {Object} UserData
 * @property {number[]} roles - An array of role IDs.
 * @property {string} base - A user generated string.
 * @property {string} department - A user generated string.
 * @property {string} rank - A user generated string.
 * @property {number} maintenance - The maintenance role/level of the user.
 * @property {string} name - The name of the user. * Required Field.
 * @property {string} email - The email of the user. * Requred Field.
 * @property {string} employeeId - A user generated string.
 * @property {string} phone - The user's contact phone number.
 * @property {string} gender - The user's gender.
 * @property {number} [birthday] - The user's birthday.
 * @property {boolean} isAdmin - Whether the user is an admin.
 * @property {boolean} serviceManager - Whether the user is a service manager.
 * @property {boolean} deploymentManager - Whether the user can manage Fusion-managed deployments (workplace + per-workplace service overrides).
 * @property {string} address - A street address.
 * @property {string} city - A city or county.
 * @property {string} state - A state.
 * @property {string} zipcode - A zipcode.
 * @property {string} eContactName - Emergency contact's name.
 * @property {string} eContactPhone - Emergency contact's phone number.
 */

/**
 * A default value for a user object.
 * @type {{roles: number[], base: string, department: string, rank: string, maintenance: number, name: string, email: string, employeeId: string, phone: string, gender: string, birthday: string, address: string, city: string, state: string, zipcode: string, eContactName: string, eContactPhone: string}}
 */
export const DEFAULT_USER_DATA = {
  roles: [],
  base: '',
  department: '',
  rank: '',
  maintenance: 0,
  isAdmin: false,
  serviceManager: 0,
  deploymentManager: 0,
  name: '',
  email: '',
  employeeId: '',
  phone: '',
  gender: 'Not Specified',
  birthday: null,
  address: '',
  city: '',
  state: '',
  zipcode: '',
  eContactName: '',
  eContactPhone: '',
};

/**
 * Displays and/or edits information of a user.
 * @param {{value: {roles: number[], base: string, department: string, rank: string, maintenance: number, name: string, email: string, employeeId: string, phone: string, gender: string, birthday: string, address: string, city: string, state: string, zipcode: string, eContactName: string, eContactPhone: string}, onChange: (value: {roles: number[], base: string, department: string, rank: string, maintenance: number, name: string, email: string, employeeId: string, phone: string, gender: string, birthday: string, address: string, city: string, state: string, zipcode: string, eContactName: string, eContactPhone: string}), disabledFields?: string[]}}
 */
export default function UserEditor({ value, onChange, disabledFields = [], onUserError }) {
  const [errorMsg, setErrorMsg] = useState({});
  const { data: allRoles = [] } = useGetAllRolesQuery();
  const userEmail = useSelector(selectUserEmail);

  const filteredRoles = allRoles
    .filter((filter) => filter.role_Id >= 0)
    .map((result) => {
      return {
        role_Id: result.role_Id,
        role_name: result.role_name,
        member: result.users?.includes(userEmail),
      };
    });

  const toggleRole = (roleId) => {
    const hasRole = value.roles.includes(roleId);
    const nextRoles = hasRole
      ? value.roles.filter((selectedRoleId) => selectedRoleId !== roleId)
      : [...value.roles, roleId];
    onChange({ ...value, roles: nextRoles });
  };

  const selectedRolesLabel = () => {
    if (!value.roles.length) {
      return disabledFields.includes('roles') ? '\u00a0' : 'Select Roles...';
    }
    if (value.roles.length > 1) return 'Multiple Roles';
    return filteredRoles.find((role) => value.roles.includes(role.role_Id))?.role_name || 'Role';
  };

  // Check for any active error messages and pass a message up to the parent component if there is one
  useEffect(() => {
    const anyError = Object.values(errorMsg).some(Boolean);
    onUserError?.(anyError ? Object.values(errorMsg) : '');
  }, [errorMsg]);

  useEffect(() => {
    if (!value?.email) return;

    // If email is disabled, skip duplicate check and clear error
    if (disabledFields.includes('email')) {
      setErrorMsg((prev) => ({ ...prev, email: '' }));
      return;
    }

    const validateEmail = async (email) => {
      try {
        const emailRegex =
          /^(?![_.-])[a-zA-Z0-9._%+-]{1,64}(?<![_.-])@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;

        if (!emailRegex.test(email)) {
          setErrorMsg((prev) => ({
            ...prev,
            email: 'Invalid email address',
          }));
          return;
        }

        const res = await checkFusionUserEmail(value.email.trim());
        setErrorMsg((prev) => ({
          ...prev,
          email: '',
        }));
        return res;
      } catch (error) {
        console.error('Error when validating email:', error);
        setErrorMsg((prev) => ({
          ...prev,
          email: 'Error validating email. Please try again later.',
        }));
      }
    };

    const debounceTimeout = setTimeout(() => {
      (async () => {
        const res = await validateEmail(value.email.trim());
        if (res && res.status === 200 && res.data.result) {
          setErrorMsg((prev) => ({
            ...prev,
            email: 'Email is already in use',
          }));
        }
      })();
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [value?.email, disabledFields.includes('email')]);

  if (value)
    return (
      <Container fluid className="d-flex flex-column gap-3 flex-xxl-row mt-3">
        <Form>
          <h2 className="mb-3 fs-4 fw-bold">Job Description</h2>
          <div className="row row-cols-1 g-3">
            <Form.Group className="d-flex flex-column w-100">
              <Form.Label className="bg-transparent fs-6" htmlFor="roles">
                Roles
              </Form.Label>
              <Dropdown autoClose="outside" className="w-100">
                <Dropdown.Toggle
                  id="roles"
                  disabled={disabledFields.includes('roles')}
                  variant="secondary"
                  className="w-100 bg-primary rounded-2 border border-1 border-secondary text-start d-flex align-items-center justify-content-between"
                  style={{ maxWidth: '100%' }}
                >
                  <span className="text-truncate">{selectedRolesLabel()}</span>
                  <FaChevronDown className="ms-2 flex-shrink-0" />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="w-100 p-1"
                  style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'hidden' }}
                >
                  {filteredRoles.map((role, index) => (
                    <Dropdown.Item
                      as="button"
                      key={index}
                      className="rounded-2"
                      onClick={(event) => {
                        event.preventDefault();
                        toggleRole(role.role_Id);
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        label={role.role_name}
                        checked={value.roles.includes(role.role_Id)}
                        onChange={() => {}}
                        readOnly
                        style={{ pointerEvents: 'none' }}
                      />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            <FusionInputField
              label="Base"
              required={true}
              disabled={disabledFields.includes('base')}
              value={value.base}
              onChange={(newValue) => onChange({ ...value, base: newValue })}
            />
            <FusionInputField
              label="Department"
              required={true}
              disabled={disabledFields.includes('department')}
              value={value.department}
              onChange={(newValue) => onChange({ ...value, department: newValue })}
            />
            <FusionInputField
              label="Rank"
              required={true}
              disabled={disabledFields.includes('rank')}
              value={value.rank}
              onChange={(newValue) => onChange({ ...value, rank: newValue })}
            />
            <BaseSelect
              label="Maintenance Access"
              isSearchable
              selectProps={{
                options: MAINTNEANCE_OPTIONS,
                value: findSelectOption(MAINTNEANCE_OPTIONS, value.maintenance),
                isDisabled: disabledFields.includes('maintenance'),
                onChange: (option) => onChange({ ...value, maintenance: option.value }),
              }}
            />
          </div>
        </Form>
        <div>
          <h2 className="mb-3 fs-4 fw-bold">Information</h2>
          <div className="row row-cols-1 row-cols-sm-2 g-3">
            <FusionInputField
              label="Name"
              required={true}
              disabled={disabledFields.includes('name')}
              value={value.name}
              onChange={(newValue) => onChange({ ...value, name: newValue })}
            />
            <div>
              <FusionInputField
                label="Email"
                required={true}
                disabled={disabledFields.includes('email')}
                value={value.email ?? ''}
                onChange={(newValue) => onChange({ ...value, email: newValue })}
              />
              {errorMsg.email && (
                <p role="alert" aria-live="polite" className={styles['error-message']}>
                  {errorMsg.email}
                </p>
              )}
            </div>
            <FusionInputField
              label="Employee ID"
              disabled={disabledFields.includes('employeeId')}
              value={value.employeeId}
              onChange={(newValue) => onChange({ ...value, employeeId: newValue })}
            />
            <FusionInputField
              label="Phone Number"
              disabled={disabledFields.includes('phone')}
              value={value.phone}
              onChange={(newValue) => onChange({ ...value, phone: newValue })}
            />
            <FusionInputField
              label="Gender"
              disabled={disabledFields.includes('gender')}
              value={value.gender}
              onChange={(newValue) => onChange({ ...value, gender: newValue })}
            />
            <FusionCustomInput label="Birthday" disabled={disabledFields.includes('birthday')}>
              <input
                disabled={disabledFields.includes('birthday')}
                type="date"
                value={value.birthday || ''}
                onChange={(e) => onChange({ ...value, birthday: e.target.value })}
              />
            </FusionCustomInput>
            <FusionInputField
              label="Home Address"
              disabled={disabledFields.includes('address')}
              value={value.address}
              onChange={(newValue) => onChange({ ...value, address: newValue })}
            />
            <FusionInputField
              label="City / County"
              disabled={disabledFields.includes('city')}
              value={value.city}
              onChange={(newValue) => onChange({ ...value, city: newValue })}
            />
            <FusionInputField
              label="State"
              disabled={disabledFields.includes('state')}
              value={value.state}
              onChange={(newValue) => onChange({ ...value, state: newValue })}
            />
            <FusionInputField
              label="Zipcode"
              disabled={disabledFields.includes('zipcode')}
              value={value.zipcode}
              onChange={(newValue) => onChange({ ...value, zipcode: newValue })}
            />
            <FusionInputField
              label="Emergency Contact Name"
              disabled={disabledFields.includes('eContactName')}
              value={value.eContactName}
              onChange={(newValue) => onChange({ ...value, eContactName: newValue })}
            />
            <FusionInputField
              label="Emergency Contact Phone"
              disabled={disabledFields.includes('eContactPhone')}
              value={value.eContactPhone}
              onChange={(newValue) => onChange({ ...value, eContactPhone: newValue })}
            />
          </div>
        </div>
      </Container>
    );
}
